import { getFruticBoxToFormatMapping } from 'common/src/api/frutic';
import { supabase } from 'common/src/supabase';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import { getShipmentBoxTypes } from 'common/src/api/shipments';
import { BoxFormatMapping, User } from 'common/src/utils/types';
import { Session } from '@supabase/supabase-js';
import { create } from 'zustand';
import { createInFlightRequestCache } from 'common/src/utils/in-flight-request';

type FruticStore = {
  loading: boolean;
  fetchedMappedAndUnmappedFormats: boolean;
  mappedFormats: BoxFormatMapping[];
  unmappedFormats: string[];
  fetchMappings: (
    speciesId: number,
    season: string,
    session: Session,
    exporterRef: string,
    userProfile: User
  ) => Promise<BoxFormatMapping[] | undefined>;
  setMappedAndUnmappedFormats: (
    mappedFormats: BoxFormatMapping[],
    unmappedFormats: string[]
  ) => void;
  saveMapping: (
    fruticCode: string,
    format: (typeof VALID_FORMATS)[number],
    exporterId: number
  ) => Promise<void>;
  updateMapping: (
    mappingId: number,
    format: (typeof VALID_FORMATS)[number]
  ) => Promise<void>;
  deleteMapping: (mappingId: number, fruticCode: string) => Promise<void>;
};

let mappingRequestVersion = 0;
let activeMappingScope: string | undefined;
const runMappingRequest = createInFlightRequestCache();

const useFruticStore = create<FruticStore>((set) => ({
  loading: false,
  fetchedMappedAndUnmappedFormats: false,
  mappedFormats: [],
  unmappedFormats: [],
  fetchMappings: async (
    speciesId,
    season,
    _session,
    _exporterRef,
    userProfile
  ) => {
    const requestVersion = ++mappingRequestVersion;
    const scope = JSON.stringify([
      _session?.user?.id,
      userProfile.id,
      userProfile.exporter_id,
      season,
      speciesId,
    ]);
    const changedScope = activeMappingScope !== scope;
    activeMappingScope = scope;
    set({
      loading: true,
      fetchedMappedAndUnmappedFormats: false,
      ...(changedScope ? { mappedFormats: [], unmappedFormats: [] } : {}),
    });
    try {
      const { boxTypes, mappings } = await runMappingRequest(
        scope,
        async () => {
          const [boxTypes, mappings] = await Promise.all([
            getShipmentBoxTypes(userProfile.exporter_id!, season, speciesId),
            getFruticBoxToFormatMapping(userProfile),
          ]);
          if (!mappings)
            throw new Error('Error fetching shipment format mappings');
          return {
            boxTypes,
            mappings: mappings as unknown as BoxFormatMapping[],
          };
        }
      );

      // Determine mapped and unmapped box types
      const allMappedTypes = new Set(mappings?.map((m) => m.frutic_code));
      const mappedTypes = mappings?.filter((m) =>
        boxTypes?.includes(m.frutic_code)
      );
      const unmappedTypes = boxTypes?.filter(
        (type: string) => !allMappedTypes.has(type)
      );

      if (requestVersion !== mappingRequestVersion) return mappedTypes || [];
      set({
        mappedFormats: mappedTypes || [],
        unmappedFormats: unmappedTypes || [],
        fetchedMappedAndUnmappedFormats: true,
      });
      return mappedTypes || [];
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      if (requestVersion === mappingRequestVersion) {
        set({ loading: false });
      }
    }
  },
  setMappedAndUnmappedFormats: (mappedFormats, unmappedFormats) =>
    set({ mappedFormats, unmappedFormats }),
  saveMapping: async (fruticCode, format, exporterId) => {
    try {
      // First check if mapping already exists
      const { data: existingMapping, error: checkError } = await supabase
        .from('frutic_box_to_format')
        .select()
        .eq('frutic_code', fruticCode)
        .eq('exporter_id', exporterId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 is "not found" error
        throw checkError;
      }

      if (existingMapping) {
        // Update existing mapping
        const updatePayload: {
          cherry_box_format: (typeof VALID_FORMATS)[number];
        } = {
          cherry_box_format: format,
        };

        const { data, error } = await supabase
          .from('frutic_box_to_format')
          .update(updatePayload)
          .eq('id', existingMapping.id)
          .select()
          .single();

        if (error) throw error;

        set((state) => ({
          mappedFormats: state.mappedFormats.map((m) =>
            m.id === existingMapping.id ? (data as BoxFormatMapping) : m
          ),
          unmappedFormats: state.unmappedFormats.filter(
            (f) => f !== fruticCode
          ),
        }));
      } else {
        // Insert new mapping
        const newMapping = {
          exporter_id: exporterId,
          frutic_code: fruticCode,
          cherry_box_format: format,
        };

        const { data, error } = await supabase
          .from('frutic_box_to_format')
          .insert(newMapping)
          .select()
          .single();

        if (error) throw error;

        set((state) => ({
          mappedFormats: [...state.mappedFormats, data as BoxFormatMapping],
          unmappedFormats: state.unmappedFormats.filter(
            (f) => f !== fruticCode
          ),
        }));
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
    }
  },
  updateMapping: async (mappingId, format) => {
    try {
      const { data, error } = await supabase
        .from('frutic_box_to_format')
        .update({
          cherry_box_format: format,
        })
        .eq('id', mappingId)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        mappedFormats: state.mappedFormats.map((m) =>
          m.id === mappingId ? (data as BoxFormatMapping) : m
        ),
      }));
    } catch (error) {
      console.error('Error updating mapping:', error);
    }
  },
  deleteMapping: async (mappingId, fruticCode) => {
    try {
      const { error } = await supabase
        .from('frutic_box_to_format')
        .delete()
        .eq('id', mappingId);

      if (error) throw error;

      set((state) => ({
        mappedFormats: state.mappedFormats.filter((m) => m.id !== mappingId),
        unmappedFormats: [...state.unmappedFormats, fruticCode],
      }));
    } catch (error) {
      console.error('Error deleting mapping:', error);
    }
  },
}));

export default useFruticStore;
